import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { format, parseISO } from "date-fns";

import { Column, Pagination, Table, Td } from "@/components/admin/table";
import { usePaged } from "@/components/admin/use-paged";
import {
  ACTIVITY_TYPE_OPTIONS,
  describe,
  type ActivityEntry,
} from "@/components/admin/activity-events";
import {
  DEFAULT_RANGE,
  RangeControl,
  rangeLabel,
  rangeParams,
  type Range,
} from "@/components/admin/range-control";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useApiHeaders } from "@/hooks/use-api-headers";
import { useAdminOrgStore } from "@/stores/admin-org";
import { API_URL, errorDetail, fetchJson } from "@/lib/admin-api";

/**
 * The site activity log (POS2-1138 CP3).
 *
 * FoodSight-internal. Every column on `/admin/customer-health` is computed from entries in this
 * feed, so this is the evidence rather than a supporting screen — and it ships one checkpoint
 * *before* the table, so that the row-click destination exists by the time CP4 links into it.
 *
 * It is its own nav entry rather than a tab of the health table (decision 26): "what happened at
 * this site last week?" is a question worth asking on its own, and shipping the log behind a table
 * that does not exist yet would make this an unreachable route.
 *
 * **English only** — the `/admin/*` carve-out from Phase 6.4.
 */

const BASE = `${API_URL}/api/v1/admin`;

export type SiteOption = {
  site_id: string;
  site_name: string;
  org_id: string;
  org_name: string;
};

/**
 * Which site the selector opens on when the URL does not say (POS2-1279).
 *
 * The context switcher is the platform admin's stated scope on every other screen, so the log
 * opening on some other organisation's site — whichever sorted first across the estate — made
 * this the one screen that ignored it. The precedence is the switcher's site, then the first
 * site of the switcher's organisation, then the first overall.
 *
 * `options` arrives ordered by site name from `/admin/activity/sites`, so "the first" is the
 * first alphabetically without this having to sort again. Org level is the empty string rather
 * than null, and it needs no special case: no row carries an empty id, so it matches nothing and
 * falls through. A switcher site that is not in the list — deleted, or since scoped away — falls
 * through the same way rather than selecting nothing.
 */
export function defaultActivitySite(
  options: readonly SiteOption[],
  switcherSiteId: string,
  switcherOrgId: string,
): string | null {
  const picked = options.find((o) => o.site_id === switcherSiteId);
  if (picked) return picked.site_id;
  const inOrg = options.find((o) => o.org_id === switcherOrgId);
  if (inOrg) return inOrg.site_id;
  return options[0]?.site_id ?? null;
}

type ActivityResponse = {
  period_from: string;
  period_to: string;
  login_history_from: string | null;
  emails: Record<string, string>;
  entries: ActivityEntry[];
};

const COLUMNS: Column[] = [
  { label: "When" },
  { label: "Actor" },
  { label: "Event" },
  { label: "Detail" },
];

const ALL_TYPES = "all";

export default function AdminActivityPage() {
  // The site and the range come from the URL so CP4's row click can hand over a whole question —
  // this site, this window — and so a link to a specific feed survives being pasted to a colleague.
  const [params, setParams] = useSearchParams();
  const [range, setRange] = useState<Range>(rangeFromParams(params));
  const [type, setType] = useState<string>(ALL_TYPES);
  const headers = useApiHeaders();
  // The scope the admin already stated in the context picker, which the selector opens on.
  const switcherSiteId = useAdminOrgStore((s) => s.siteId);
  const switcherOrgId = useAdminOrgStore((s) => s.orgId);

  const sites = useQuery<SiteOption[]>({
    queryKey: ["admin-activity-sites"],
    queryFn: () => fetchJson<SiteOption[]>(`${BASE}/activity/sites`, headers),
  });

  const siteId =
    params.get("site") ?? defaultActivitySite(sites.data ?? [], switcherSiteId, switcherOrgId);

  const query = useMemo(
    () => ({ ...rangeParams(range), ...(type === ALL_TYPES ? {} : { types: type }) }),
    [range, type],
  );

  const feed = useQuery<ActivityResponse>({
    queryKey: ["admin-activity", siteId, query],
    enabled: siteId !== null,
    queryFn: () =>
      fetchJson<ActivityResponse>(
        `${BASE}/activity?${new URLSearchParams({ site_id: siteId!, ...query })}`,
        headers,
      ),
  });

  const paged = usePaged(feed.data?.entries ?? []);
  const site = sites.data?.find((option) => option.site_id === siteId);

  // Decision 34's note. The seed gives each user one historical row, so a range beginning before
  // real capture started shows only each user's most recent sign-in before that date — which is a
  // plausible-looking undercount rather than an obvious gap, and the worse of the two.
  const historyGap =
    feed.data &&
    (!feed.data.login_history_from ||
      feed.data.period_from < feed.data.login_history_from.slice(0, 10));

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-xl font-extrabold tracking-tight text-gray-900">
            Activity
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {site ? `${site.site_name} · ${site.org_name}` : "Every site, every organisation"}
            {feed.data
              ? ` · ${rangeLabel(feed.data.period_from, feed.data.period_to)}`
              : " · complete days only"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={siteId ?? ""}
            onValueChange={(value) => {
              // One updater. Two synchronous setSearchParams calls race — the second reads stale
              // params and clobbers the first (react-router v7).
              const next = new URLSearchParams(params);
              next.set("site", value);
              setParams(next, { replace: true });
            }}
          >
            <SelectTrigger className="w-64" aria-label="Site">
              <SelectValue placeholder="Select a site" />
            </SelectTrigger>
            <SelectContent>
              {(sites.data ?? []).map((option) => (
                <SelectItem key={option.site_id} value={option.site_id}>
                  {option.site_name} · {option.org_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-52" aria-label="Event type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_TYPES}>All events</SelectItem>
              {ACTIVITY_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <RangeControl
            range={range}
            onChange={setRange}
            resolved={
              feed.data ? { from: feed.data.period_from, to: feed.data.period_to } : undefined
            }
          />
        </div>
      </div>

      {/* Stated on the screen rather than in a comment: until POS2-1136's scheduler is enabled,
          the send-driven types simply never occur. An empty feed that says nothing reads as
          "nothing happened here", which is a different claim. POS2-1234 wrote the emitters for
          schedule and recipient edits, so this no longer says they are unrecorded — a note that
          tells an admin the log is missing something it now carries is worse than no note. */}
      <p className="mb-3 text-xs text-gray-500">
        Logins, device transitions and schedule and recipient edits are recorded for every site.
        Send and download events appear once a schedule has sent.
      </p>

      {/* An empty feed and a failed request are different facts, and the table can only render one
          of them. "No activity in this range." on a 400 or a dropped connection reads as *nothing
          happened at this site* — the same class of lie the range validation exists to prevent, so
          it would be odd to introduce it here. */}
      {feed.isError || sites.isError ? (
        <p
          role="alert"
          data-testid="activity-error"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {errorDetail(feed.error ?? sites.error)}
        </p>
      ) : (
        <Table<ActivityEntry>
          columns={COLUMNS}
          rows={paged.page}
          isLoading={feed.isLoading || sites.isLoading}
          empty="No activity in this range."
          renderRow={(entry, columns) => {
            const descriptor = describe(entry.type);
            const Icon = descriptor.icon;
            return (
              <tr key={entry.id} className="border-b border-black/[0.06] last:border-b-0">
                <Td column={columns[0]} className="whitespace-nowrap tabular-nums">
                  {format(parseISO(entry.occurred_at), "dd/MM/yy HH:mm")}
                </Td>
                <Td column={columns[1]}>
                  {entry.actor_user_id ? (feed.data?.emails[entry.actor_user_id] ?? "—") : "System"}
                </Td>
                <Td column={columns[2]}>
                  <span className="inline-flex items-center gap-2">
                    <Icon className="size-4 text-gray-400" strokeWidth={1.5} aria-hidden />
                    {descriptor.label}
                  </span>
                </Td>
                <Td column={columns[3]} className="text-gray-500">
                  {/* An org-wide row is a login the organisation's user made, not something that
                    happened in this kitchen. Saying so is what stops the feed reading as though
                    somebody walked in. */}
                  {entry.is_org_wide && entry.type === "user.login"
                    ? [descriptor.detail(entry.payload), "signed in to the organisation"]
                        .filter(Boolean)
                        .join(" · ")
                    : (descriptor.detail(entry.payload) ?? "—")}
                </Td>
              </tr>
            );
          }}
          footer={paged.bar ? <Pagination {...paged.bar} /> : undefined}
        />
      )}

      {historyGap && (
        <p className="mt-3 text-xs text-gray-500" data-testid="login-history-note">
          {feed.data?.login_history_from
            ? `Login history starts ${format(parseISO(feed.data.login_history_from), "dd/MM/yy")}. Earlier ranges show only each user's most recent sign-in before that date.`
            : "Login history has not started yet. Sign-ins before this release show only each user's most recent one."}
        </p>
      )}
    </div>
  );
}

/** A range handed over in the URL, so CP4's row click can open this feed on the same window. */
function rangeFromParams(params: URLSearchParams): Range {
  const from = params.get("from");
  const to = params.get("to");
  if (from && to) return { preset: "custom", from, to };
  const preset = params.get("preset");
  if (preset === "yesterday" || preset === "14d" || preset === "7d") {
    return { preset, from: null, to: null };
  }
  return DEFAULT_RANGE;
}
